import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { DialogActions, DialogContent } from '@startuptools/ui/Dialog';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import { useCreateShareIssueMutation } from './ShareIssues.graphql';
import { GqlSiCreateInput } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GenericError } from '@startuptools/ui/Error';
import { ApolloError } from '@apollo/client';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { useNavigate } from '../../components/Navigate';
import { siLink } from '../../components/Links';

export const CreateShareIssueForm = ({ onClose }: { onClose: () => void }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [createSi, { loading, error }] = useCreateShareIssueMutation();
  const time = DateTime.local();
  const { navigate } = useNavigate();
  const { control, handleSubmit, setError } = useForm<GqlSiCreateInput>({
    defaultValues: {
      description: i18n._('Share issue {season} {year}', {
        season: time.month <= 6 ? i18n._('spring') : i18n._('autumn'),
        year: time.year,
      }),
      primaryLang: true,
      secondaryLang: false,
      preferentialRights: false,
      shareClasses: [],
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async data => {
        try {
          const response = await createSi({ variables: { companyId, input: data } });
          const si = response.data?.createShareIssue;
          if (si) {
            return navigate(siLink(si));
          }
        } catch (err) {
          if (err instanceof ApolloError && err.message?.includes('DUPLICATE_DESCRIPTION')) {
            setError('description', {
              type: 'manual',
              message: i18n._('A share issue with this description already exists'),
            });
            return;
          }
          throw err;
        }
      })}
    >
      <DialogContent sx={{ minWidth: '400px' }}>
        <Controller
          control={control}
          name="description"
          rules={{
            required: {
              value: true,
              message: i18n._('Description is required'),
            },
          }}
          render={({ field, formState }) => (
            <FormControl error={Boolean(formState.errors?.description)}>
              <FormLabel>
                <Trans id="Description" />
              </FormLabel>
              <Input {...field} />
              {formState.errors.description && <FormHelperText>{formState.errors.description.message}</FormHelperText>}
            </FormControl>
          )}
        />

        {error && !error.message?.includes('DUPLICATE_DESCRIPTION') && <GenericError />}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          <Trans id="Cancel" />
        </Button>
        <Button type="submit" loading={loading}>
          <Trans id="Continue" />
        </Button>
      </DialogActions>
    </form>
  );
};
